import { ErrorState } from '@/components/ui/async-state';
import { Icon } from '@/components/ui/icon';
import { Skeleton } from '@/components/ui/skeleton';
import { AppScreen, IconAction } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { WorkspaceAccessState } from '@/features/workspaces/WorkspaceAccessState';
import { useAuth } from '@/lib/auth/auth';
import type { OrgActivityEventType, OrgActivityItem } from '@/lib/api/apiTypes';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { useOrganization } from '@/features/workspaces/useOrganization';
import { useOrganizationActivity } from '@/features/workspaces/useOrganizationActivity';
import { useWorkspaceMode } from '@/features/workspaces/useWorkspaceMode';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { cn } from '@/lib/utils';
import { Redirect, router } from 'expo-router';
import {
  ArrowLeftIcon,
  BadgeCheckIcon,
  Building2Icon,
  HistoryIcon,
  LockKeyholeIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserCogIcon,
  UserMinusIcon,
  UserPlusIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

const ALL_VENUES_LABEL = 'All venues';

export default function BusinessActivityScreen() {
  const session = useAuth((state) => state.session);
  const workspaceMode = useWorkspaceMode();
  const { currentContext, currentBusinessMembership } = useAccountContext();
  const organizationID =
    currentContext?.kind === 'business' ? currentContext.activeOrgId : currentBusinessMembership?.organization_id ?? null;
  const organizationQuery = useOrganization(organizationID ?? '', workspaceMode.orgAccessEnabled && Boolean(organizationID));
  const [selectedVenueID, setSelectedVenueID] = React.useState<string | null>(null);
  const activityQuery = useOrganizationActivity(
    organizationID ?? '',
    selectedVenueID,
    workspaceMode.orgAccessEnabled && Boolean(organizationID)
  );
  const viewerRole = currentBusinessMembership?.role ?? organizationQuery.data?.viewer_role;
  const canViewActivity = viewerRole === 'owner' || viewerRole === 'manager';

  if (!session) {
    return <Redirect href={{ pathname: '/sign-in', params: { next: '/(business)/activity' } }} />;
  }

  if (!workspaceMode.orgAccessEnabled || !organizationID) {
    return <Redirect href={'/workspaces' as never} />;
  }

  return (
    <AppScreen>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>
        <View className="gap-6">
          <View className="flex-row items-center justify-between">
            <IconAction icon={ArrowLeftIcon} onPress={() => router.back()} />
            <View className="flex-1 px-3">
              <Text className="text-3xl font-extrabold tracking-tight text-foreground">Activity log</Text>
              <Text variant="muted" className="mt-1">
                {organizationQuery.data?.name ?? currentBusinessMembership?.organization_name ?? 'Organization'}
              </Text>
            </View>
            <View className="size-11" />
          </View>

          {!canViewActivity ? (
            <WorkspaceAccessState
              icon={LockKeyholeIcon}
              title="Activity log is limited"
              body="Only owners and managers can open organization history. Your current role still has access to the venue spaces assigned to you."
            />
          ) : null}

          {canViewActivity && organizationQuery.data?.venues && organizationQuery.data.venues.length > 1 ? (
            <View className="gap-3">
              <Text className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                All venues
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 12 }}>
                <ActivityFilterChip active={!selectedVenueID} onPress={() => setSelectedVenueID(null)}>
                  {ALL_VENUES_LABEL}
                </ActivityFilterChip>
                {organizationQuery.data.venues.map((venue) => (
                  <ActivityFilterChip
                    key={venue.venue_id}
                    active={selectedVenueID === venue.venue_id}
                    onPress={() => setSelectedVenueID(venue.venue_id)}>
                    {venue.venue_name}
                  </ActivityFilterChip>
                ))}
              </ScrollView>
            </View>
          ) : null}

          {canViewActivity && activityQuery.isPending ? (
            <View className="gap-5 pt-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <ActivitySkeletonRow key={index} />
              ))}
            </View>
          ) : null}

          {canViewActivity && activityQuery.isError ? (
            <ErrorState
              title="Couldn't load activity."
              body={getAPIErrorRecoveryCopy(
                activityQuery.error,
                'workspace',
                'Unable to load organization history.'
              )}
              actionLabel="Retry"
              onAction={() => activityQuery.refetch()}
            />
          ) : null}

          {canViewActivity && !activityQuery.isPending && !activityQuery.isError ? (
            (activityQuery.data?.activity.length ?? 0) > 0 ? (
              <View className="pt-2">
                {activityQuery.data?.activity.map((item, index) => (
                  <ActivityRow
                    key={item.id}
                    item={item}
                    venueName={organizationQuery.data?.venues.find((venue) => venue.venue_id === item.venue_id)?.venue_name}
                    className={index > 0 ? 'border-t border-border/70' : undefined}
                  />
                ))}
              </View>
            ) : (
              <View className="items-center justify-center pt-18">
                <Icon as={HistoryIcon} size={28} className="text-muted-foreground" />
                <Text variant="muted" className="mt-4 text-center">
                  Team changes, invite history, and setup milestones will show up here.
                </Text>
              </View>
            )
          ) : null}
        </View>
      </ScrollView>
    </AppScreen>
  );
}

function ActivityFilterChip({
  active,
  children,
  onPress,
}: {
  active: boolean;
  children: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      className={cn(
        'rounded-full border px-6 py-4',
        active ? 'border-primary/35 bg-primary/15' : 'border-border bg-surface'
      )}>
      <Text className={cn('text-lg font-semibold', active ? 'text-primary' : 'text-foreground')}>{children}</Text>
    </Pressable>
  );
}

function ActivitySkeletonRow() {
  return (
    <View className="flex-row items-start gap-4">
      <Skeleton className="mt-0.5 size-12 rounded-full bg-surface-alt" />
      <View className="flex-1 gap-2">
        <Skeleton className="h-4 w-40 rounded-full bg-surface-alt" />
        <Skeleton className="h-4 w-[84%] rounded-full bg-surface-alt" />
        <Skeleton className="h-3 w-32 rounded-full bg-surface-alt" />
      </View>
    </View>
  );
}

function ActivityRow({
  item,
  venueName,
  className,
}: {
  item: OrgActivityItem;
  venueName?: string;
  className?: string;
}) {
  const tone = activityTone(item.type);

  return (
    <View className={cn('flex-row items-start gap-4 py-5', className)}>
      <View className={cn('mt-0.5 size-12 items-center justify-center rounded-full border', tone.avatarClassName)}>
        <Icon as={tone.icon} size={18} className={tone.iconClassName} />
      </View>
      <View className="flex-1">
        <View className="flex-row flex-wrap items-baseline gap-x-2 gap-y-1">
          <Text className="text-lg font-semibold text-foreground">{item.actor_display_name}</Text>
          <Text variant="small" className="text-muted-foreground">
            {formatActivityAge(item.created_at)}
          </Text>
        </View>
        <Text className="mt-1 text-base leading-7 text-foreground">{item.primary_text}</Text>
        {item.secondary_text || venueName ? (
          <Text variant="small" className="mt-1 text-muted-foreground">
            {[item.secondary_text, venueName].filter(Boolean).join(' · ')}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function activityTone(type: OrgActivityEventType) {
  switch (type) {
    case 'organization_invite_sent':
      return {
        icon: UserPlusIcon,
        avatarClassName: 'border-primary/20 bg-primary/10',
        iconClassName: 'text-primary',
      };
    case 'organization_invite_accepted':
      return {
        icon: UserPlusIcon,
        avatarClassName: 'border-emerald-500/20 bg-emerald-500/10',
        iconClassName: 'text-emerald-400',
      };
    case 'organization_member_role_updated':
    case 'organization_member_scope_updated':
      return {
        icon: UserCogIcon,
        avatarClassName: 'border-amber-500/20 bg-amber-500/10',
        iconClassName: 'text-amber-300',
      };
    case 'organization_member_removed':
    case 'organization_invite_revoked':
      return {
        icon: UserMinusIcon,
        avatarClassName: 'border-destructive/20 bg-destructive/10',
        iconClassName: 'text-destructive',
      };
    case 'owner_handoff_started':
      return {
        icon: ShieldCheckIcon,
        avatarClassName: 'border-primary/20 bg-primary/10',
        iconClassName: 'text-primary',
      };
    case 'event_boost_started':
      return {
        icon: SparklesIcon,
        avatarClassName: 'border-orange-500/20 bg-orange-500/10',
        iconClassName: 'text-orange-400',
      };
    case 'owner_setup_completed':
    case 'venue_setup_completed':
    case 'venue_claim_approved':
      return {
        icon: BadgeCheckIcon,
        avatarClassName: 'border-emerald-500/20 bg-emerald-500/10',
        iconClassName: 'text-emerald-400',
      };
    default:
      return {
        icon: Building2Icon,
        avatarClassName: 'border-border bg-surface',
        iconClassName: 'text-muted-foreground',
      };
  }
}

function formatActivityAge(value: string) {
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.max(1, Math.floor(diffMs / 60000));
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
